import { useQuery } from "@apollo/client";
import { useParams } from "react-router-dom";

import { PreviewBody, type PreviewBodyData } from "@/components/preview-body";
import { FullPageLoading } from "@/components/full-page-loading";
import {
  GET_PUBLISHED_PREVIEW,
  type GetPublishedPreviewData,
  type GetPublishedPreviewVariables,
} from "@/graphql";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useEffect } from "react";

export function PublicPreviewPage() {
  const { username = "" } = useParams<{ username: string }>();
  useDocumentTitle(username ? `@${username}` : "Sked Link");

  const { data, loading, error } = useQuery<
    GetPublishedPreviewData,
    GetPublishedPreviewVariables
  >(GET_PUBLISHED_PREVIEW, {
    variables: { instagramUsername: username },
    skip: !username,
  });

  // Inject custom CSS supplied by the published preview, if any.
  const css = data?.publishedPreview?.css ?? null;
  useEffect(() => {
    if (!css) return;
    const styleEl = document.createElement("style");
    styleEl.setAttribute("data-sked-preview-css", "published");
    styleEl.textContent = css;
    document.head.appendChild(styleEl);
    return () => {
      document.head.removeChild(styleEl);
    };
  }, [css]);

  if (loading) return <FullPageLoading />;

  if (error || !data?.publishedPreview) {
    return (
      <div className="mx-auto max-w-2xl px-8 py-16 text-center">
        <p className="font-inter text-sm text-red-600">
          We couldn&apos;t load this Sked Link.
        </p>
      </div>
    );
  }

  const published = data.publishedPreview;
  const previewData: PreviewBodyData = {
    instagramUsername: published.instagramUsername,
    instagramProfileImageUrl: published.instagram?.profileImageUrl ?? null,
    hasProfileHeader: published.hasProfileHeader,
    hasIntroduction: published.hasIntroduction,
    introductionText: published.introductionText,
    buttons: published.buttons,
    hasGallery: published.hasGallery,
    galleryTitle: published.galleryTitle,
    galleryId: published.galleryId,
    css: published.css,
  };

  return (
    <div className="sked-preview min-h-screen">
      <PreviewBody data={previewData} />
    </div>
  );
}
